import {format, parse} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {useEffect, useState} from 'react';

import {QueryParamsEnum} from 'presentation/constants';
import {ShiftTypeEnum} from 'presentation/modules/booking/exam/constants/shiftTypeEnum';
import {useIsAdvanceDiagnosticsExamsJourney} from 'presentation/modules/booking/exam/hooks/useIsAdvanceDiagnosticsExamsJourney';
import {getExamStepPosition} from 'presentation/modules/booking/exam/utils/getExamStepPosition';
import {
	MANUAL_DISTANCE_ORIGIN,
	MAX_DISTANCE_FILTER_UNITS,
} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useDisabledSelectionDateStore} from 'presentation/modules/booking/shared/store/useDisabledSelectionDateStore';
import {useExternalOpenDateModalStore} from 'presentation/modules/booking/shared/store/useExternalOpenDateModalStore';
import {useOpenDrawerFilterStore} from 'presentation/modules/booking/shared/store/useOpenDrawerFilterStore';
import {useDistanceSliderStore} from 'presentation/modules/booking/shared/ui/components/DistanceSlider/store';
import {createPeriodsConfig} from 'presentation/modules/booking/shared/utils/createPeriodsConfig';
import {useQueryParams} from 'presentation/pages';
import {useGetExamsDatesQuery} from '../../../../pages/ExamBooking/steps/ExamProfessionalSelection/queries/useGetExamsDatesQuery';
import {UseSearchBarProps} from './types';
import {MIN_DISTANCE_IN_KM_EXAM} from './constants';

export const useExamSearchBar = ({activeStep}: UseSearchBarProps) => {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const isAdvanceDiagnosticsFlow = useIsAdvanceDiagnosticsExamsJourney();
	const {SEARCH_PARAMS, setSearchParams, clearSearch} = useQueryParams();

	const {dateAt, isManualLocation} = bookingData;

	const {query, shift, localityDistance, autocompleteQuery} = SEARCH_PARAMS;

	const disabledSelectionDate = useDisabledSelectionDateStore(
		state => state.disabledSelectionDate,
	);

	const externalOpenDateModal = useExternalOpenDateModalStore(
		state => state.externalOpenDateModal,
	);
	const setExternalOpenDateModal = useExternalOpenDateModalStore(
		state => state.setExternalOpenDateModal,
	);
	const openDrawerState = useOpenDrawerFilterStore(state => state.openDrawerState);
	const setOpenDrawerState = useOpenDrawerFilterStore(state => state.setOpenDrawerState);
	const distanceSlider = useDistanceSliderStore(state => state.distanceSlider);
	const setDistanceSlider = useDistanceSliderStore(state => state.setDistanceSlider);
	const [shiftSelected, setShiftSelected] = useState<ShiftTypeEnum>(
		(shift as ShiftTypeEnum) ?? ShiftTypeEnum.ALL,
	);

	const {PROFESSIONAL_STEP, UNIT_STEP, SPECIALITY_OR_EXAM_STEP} = getExamStepPosition({
		isManualLocation,
	});

	const showProfessionalFilters = activeStep === PROFESSIONAL_STEP;
	const showSearchField = !(isAdvanceDiagnosticsFlow && showProfessionalFilters);

	const showFilterByLetter = !query && activeStep === SPECIALITY_OR_EXAM_STEP;

	const showDistanceDrawer = Boolean(activeStep === UNIT_STEP);
	const distanceFilterIsActive = localityDistance;
	const showFilter = showDistanceDrawer;
	const filterIsActive = distanceFilterIsActive;
	const distanceFilterAtMaxDistance = Number(localityDistance) >= MAX_DISTANCE_FILTER_UNITS;

	const {
		data: availableDatesList,
		isFetching: availableDatesLoading,
		isError: availableDatesError,
	} = useGetExamsDatesQuery({enabled: activeStep === PROFESSIONAL_STEP});

	const shouldClearShiftFilter = activeStep === UNIT_STEP;

	const currentSelectedDate = dateAt ? parse(dateAt, 'P', 0, {locale: ptBR}) : undefined;

	const availableDatesLoadingOrError = availableDatesLoading || availableDatesError;
	const availableDates = availableDatesLoadingOrError ? [] : availableDatesList;

	const showCurrentLocation = activeStep === UNIT_STEP;

	const distanceValue = useDistanceSliderStore(state => state.distanceSlider);
	const distanceLabel = `${distanceValue}km`;

	const PERIODS = createPeriodsConfig({
		shiftSelected,
		onShiftChange: (shift: ShiftTypeEnum) => setShiftSelected(shift),
	});

	const handleChangeDate = (selectedDate: Date | null) => {
		setSearchParams(prev => {
			if (selectedDate) {
				setBookingData(prevValue => ({
					...prevValue,
					dateAt: format(selectedDate, 'P', {locale: ptBR}),
				}));

				prev.set(QueryParamsEnum.CALENDAR_ACTIVE, 'true');
			} else {
				setBookingData(prevValue => ({
					...prevValue,
					dateAt: '',
				}));

				prev.delete(QueryParamsEnum.CALENDAR_ACTIVE);
			}

			return prev;
		});
		setExternalOpenDateModal(false);
	};

	const handleShiftButton = (shiftType: ShiftTypeEnum | string) => {
		setSearchParams(
			prev => {
				prev.set(QueryParamsEnum.SHIFT, shiftType);
				return prev;
			},
			{
				replace: true,
			},
		);
	};

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const handleCloseDrawer = (abandon?: boolean) => {
		setOpenDrawerState(false);
		if (abandon) {
			setShiftSelected(ShiftTypeEnum.ALL);
			setDistanceSlider(Number(localityDistance));
		}
	};

	const clearSearchParams = () => {
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);

				return prev;
			},
			{
				replace: true,
			},
		);
	};

	const handleClearShiftAndSearch = () => {
		setShiftSelected(ShiftTypeEnum.ALL);
		handleShiftButton(ShiftTypeEnum.ALL);
		handleCloseDrawer();
		clearSearchParams();
		if (clearSearch) {
			clearSearch();
		}
		setSearchParams(prev => {
			setBookingData(prevValue => ({
				...prevValue,
				dateAt: '',
			}));

			prev.set(QueryParamsEnum.SEARCH_QUERY, 'clear');
			prev.delete(QueryParamsEnum.CALENDAR_ACTIVE);
			if (autocompleteQuery) prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
			return prev;
		});
	};

	const handleClearShift = () => {
		setShiftSelected(ShiftTypeEnum.ALL);
		handleShiftButton(ShiftTypeEnum.ALL);
		handleCloseDrawer();
	};

	const handleConfirmShift = () => {
		handleShiftButton(shiftSelected);
		handleCloseDrawer();
	};

	const handleClearDistance = () => {
		setDistanceSlider(25);
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.DISTANCE_ORIGIN);

			if (isManualLocation) {
				prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);
			} else {
				prev.set(QueryParamsEnum.LOCALITY_DISTANCE, MIN_DISTANCE_IN_KM_EXAM);
			}
			return prev;
		});
		handleCloseDrawer();
	};

	const handleConfirmDistance = () => {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.LOCALITY_DISTANCE, distanceSlider.toString());
			prev.set(QueryParamsEnum.DISTANCE_ORIGIN, MANUAL_DISTANCE_ORIGIN);
			return prev;
		});

		handleCloseDrawer();
	};

	useEffect(() => {
		if (shouldClearShiftFilter) {
			handleClearShift();
		}
	}, [activeStep]);

	useEffect(() => {
		if (SEARCH_PARAMS.shift === '') {
			handleCloseDrawer(!openDrawerState);
		}
	}, [SEARCH_PARAMS.shift]);

	return {
		openDrawerState,
		handleOpenDrawer,
		handleCloseDrawer,
		disabledSelectionDate,
		showProfessionalFilters,
		showSearchField,
		showFilterByLetter,
		availableDatesList,
		handleChangeDate,
		PERIODS,
		handleConfirmShift,
		handleClearShift,
		handleClearShiftAndSearch,
		dateAt,
		shift,
		showFilter,
		filterIsActive,
		showDistanceDrawer,
		handleClearDistance,
		handleConfirmDistance,
		distanceFilterAtMaxDistance,
		showCurrentLocation,
		availableDates,
		currentSelectedDate,
		externalOpenDateModal,
		setExternalOpenDateModal,
		distanceLabel,
	};
};
